import { motion } from "framer-motion";
import { ArrowRight, Phone, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { MaskReveal, Reveal } from "../components/Reveal";
import { useContactDialogs } from "../components/ContactDialogs";
import { PHONE, PHONE_DISPLAY, PORTRAIT_URL } from "../data/constants";

const VALUES = [
  {
    n: "01",
    title: "Teljes banki piacismeret",
    body: "A hazai piacon jelen lévő összes bankkal partnerségben állok. Nem kell bankról bankra járnia; egyetlen találkozó alkalmával a teljes hitelpiaci kínálatot az asztalra teszem. A bankokat megversenyeztetve kizárólag az Ön érdekeit képviselem — legyen szó lakáshitelről, személyi kölcsönről vagy vállalati finanszírozásról.",
  },
  {
    n: "02",
    title: "Profi biztosítási megoldások",
    body: "A hitelek mellett átfogó segítséget nyújtok a biztosítások területén is. Személyre szabott védelmet kínálok az élet-, vagyon- és gépjármű-biztosítások piacán, hogy otthona, családja és értékei a legoptimálisabb díjak mellett legyenek biztonságban.",
  },
  {
    n: "03",
    title: "Helyi fókusz, országos lefedettség",
    body: "Kiemelt fókuszterületem Szabolcs-Szatmár-Bereg megye, így a régióban személyes, dedikált figyelemmel állok rendelkezésére. A modern online folyamatoknak köszönhetően azonban országosan bárhol zökkenőmentesen lebonyolítom a teljes ügyintézést.",
  },
  {
    n: "04",
    title: "Időt és pénzt spórolok Önnek",
    body: "Átvállalom a teljes adminisztrációt, a dokumentációk bekérésétől egészen a szerződéskötésig. Olyan érzelmi és racionális biztonságot nyújtok a döntésében, amely hosszú távú bizalomra épül.",
  },
];

function Portrait() {
  return (
    <div data-testid="about-portrait" className="relative w-full max-w-sm mx-auto">
      <div className="relative aspect-[4/5] border border-foreground overflow-hidden bg-secondary">
        {PORTRAIT_URL ? (
          <img src={PORTRAIT_URL} alt="Szabó Ádám" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center gap-4">
            <span className="font-serif-display text-7xl text-muted-foreground/40">SZÁ</span>
            <span className="overline text-muted-foreground/70">Fénykép helye</span>
          </div>
        )}
        <div className="noise-overlay" />
      </div>
      <div className="mt-5 flex items-baseline justify-between border-t border-border pt-4">
        <span className="font-serif-display text-xl">Szabó Ádám</span>
        <span className="overline text-muted-foreground">Pénzügyi szakértő</span>
      </div>
    </div>
  );
}

export default function About() {
  const { openBooking } = useContactDialogs();
  return (
    <div data-testid="about-page" className="pt-40">
      {/* Intro */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-20">
        <span className="overline text-muted-foreground">Bemutatkozás</span>
        <h1 className="font-serif-display font-medium tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-4xl">
          <MaskReveal lines={["Az Ön független pénzügyi", "partnere Szabolcs megyében"]} delay={0.2} />
          <MaskReveal lines={["– és országosan."]} delay={0.45} lineClassName="italic text-muted-foreground" />
        </h1>
      </section>

      {/* Portrait + intro text */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Portrait />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
              className="text-lg text-muted-foreground leading-relaxed">
              Pénzügyi szakértőként az a célom, hogy Ön a lehető legkedvezőbb és legbiztonságosabb
              döntéseket hozhassa meg. A pénzügyekben a tiszta, egyenes beszéd híve vagyok:
              felesleges körök, apróbetűs részek és bonyolult banki zsargon helyett érthető,
              gyors és lényegre törő megoldásokat kínálok.
            </motion.p>
            <div className="mt-10">
              <span className="overline text-muted-foreground">Az elveim</span>
              <ul className="mt-5 space-y-4">
                {["Tiszta, egyenes beszéd — apróbetűs meglepetések nélkül.",
                  "Kizárólag az Ön érdekét képviselem, nem a bankokét.",
                  "A teljes adminisztrációt leveszem a válláról."].map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="overline text-muted-foreground">Előnyök</span>
              <Reveal>
                <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight mt-4">
                  Miben nyújtok többet egy hagyományos bankfióknál?
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-10">
              {VALUES.map((v, i) => (
                <Reveal key={v.n} delay={(i % 2) * 0.1}>
                  <div className="border-t border-foreground pt-6">
                    <span className="font-serif-display text-4xl text-muted-foreground/40">{v.n}</span>
                    <h3 className="font-serif-display text-xl mt-4">{v.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">{v.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal>
            <div className="mt-20 border-t border-border pt-10 flex items-baseline gap-4">
              <span className="font-serif-display text-2xl">Szabó Ádám</span>
              <span className="overline text-muted-foreground">Pénzügyi szakértő</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground text-background">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tight max-w-xl">
            Ismerkedjünk meg személyesen.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button data-testid="about-booking-btn" onClick={openBooking}
              className="rounded-none bg-background text-foreground hover:bg-background/90 h-14 px-8 group">
              Időpontfoglalás
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <a href={`tel:${PHONE}`} data-testid="about-call-link"
              className="inline-flex items-center gap-2 text-background/80 hover:text-background transition-colors h-14 px-2">
              <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
